import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'store/store';
import { storeDetailsSelector } from 'store/selectors/locationServicesSelector';
import { AlertBanner, TextLink } from '@dsg-tech/homefield-react';
import { setisSkuSelectionUndefined } from 'store/slices/common';
import {
  storeIdentifierNameSelector,
  isSkuSelectionUndefinedSelector,
  getFeatureFlagByNameSelector,
} from 'store/selectors';
import { promotionDataSelector, templateSelector } from 'store/selectors/quickViewSelector';
import { quickViewFullPDPClickEvent } from 'components/Quickview/analytics/analytics-events';
import ProductPromotion from '../PromoMessage';
import Badge from '../Badge/Badge';
import ImageViewer from '../ImageViewer/ImageViewer';
import Price from '../Price';
import FulfillmentOptions from '../FulfillmentOptions/FulfillmentOptions';
import Attributes from '../Attributes/Attributes';
import AddToCart from '../ActionButtons/AddToCart';
import PreOrderMessaging from '../PreOrderMessaging/PreOrderMessaging';
import AddToFavorites from '../ActionButtons/AddToFavorites';
import UpdateProduct from '../ActionButtons/UpdateProduct';
import useProductStock from '../../hooks/useProductStock';
import { checkComingSoonEndDate } from '../../utility/utils';
import { QuickviewDetails, SkuDetails } from '../FulfillmentOptions/types';

type QUickViewContentProps = {
  quickviewDetails: QuickviewDetails;
  color: string;
  loading: boolean;
  sku: SkuDetails | null | undefined;
  setColor: () => void;
  setSKU: () => void;
  validate: boolean;
  setValidate: () => void;
  setCurrentSkuInfo: () => void;
  updateSignal: () => void;
  skuDefaultValue: SkuDetails;
  count: number;
  selectedMethod: string;
  setSelectedMethod: () => void;
  callback: () => void;
  onClose: () => void;
};

const QuickViewContent = ({
  quickviewDetails,
  color,
  loading,
  sku,
  setColor,
  setSKU,
  validate,
  setValidate,
  setCurrentSkuInfo,
  updateSignal,
  skuDefaultValue,
  count,
  selectedMethod,
  setSelectedMethod,
  callback,
  onClose,
}: QUickViewContentProps) => {
  const dispatch = useAppDispatch();
  const isSkuSelectionUndefined = useAppSelector(isSkuSelectionUndefinedSelector);
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const template = useAppSelector(templateSelector);
  const promotionData = useAppSelector(promotionDataSelector);
  const storeDetails = useAppSelector(storeDetailsSelector);

  const { style } = quickviewDetails;

  const footerRef = useRef(null);

  const [showFulfillmentOptions, setShowFulfillmentOptions] = useState(true);
  const [attributesState, setAttributesState] = useState({});
  const [redirect, setRedirect] = useState(false);
  const [contentHeight, setContentHeight] = useState('auto');

  const { isProductOOS } = useProductStock(sku, quickviewDetails, count);

  const quickViewDrawerEnabled = useAppSelector(
    getFeatureFlagByNameSelector('quickViewDrawerEnabled'),
  );

  const quickViewproductNotAvailableImprovements = useAppSelector(
    getFeatureFlagByNameSelector('QVproductNotAvailableImprovements'),
  );

  const getAttribute = (identifier: string, value: string) => {
    if (
      quickviewDetails.style.descriptiveAttributes &&
      Array.isArray(quickviewDetails.style.descriptiveAttributes)
    ) {
      return quickviewDetails.style.descriptiveAttributes.find(
        (attribute) => attribute.identifier === identifier && attribute.value === value,
      );
    }
    return false;
  };

  useEffect(() => {
    if (quickviewDetails?.style) {
      setRedirect(
        quickviewDetails.style.akamaiRedirect ||
          getAttribute('4973', 'Personalized') ||
          getAttribute('4749', 'Y') ||
          getAttribute('5632', 'Nike APE Exclusive'),
      );
    }
  }, [quickviewDetails]);

  useEffect(() => {
    setCurrentSkuInfo(attributesState);
  }, [attributesState]);

  useEffect(() => {
    if (sku === undefined) {
      dispatch(setisSkuSelectionUndefined(true));
    } else {
      dispatch(setisSkuSelectionUndefined(false));
    }

    if (sku && sku !== null) {
      if (
        sku.comingSoonEndDate &&
        sku.comingSoonEndDate !== null &&
        checkComingSoonEndDate(sku.comingSoonEndDate)
      ) {
        setShowFulfillmentOptions(false);
      } else setShowFulfillmentOptions(true);
    }
  }, [sku]);

  useEffect(() => {
    const calculateHeight = () => {
      const footerHeight = footerRef.current?.offsetHeight ?? 0;
      const viewportHeight = window.innerHeight;
      // 80 is the header height
      setContentHeight(`${viewportHeight - (footerHeight + 80)}px`);
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  const showAlertBanner = isSkuSelectionUndefined ?? (template === 'edit' && isProductOOS);

  const key = style?.partNumber ?? '';

  const showDetailsLink = redirect && template === 'cart';

  const viewFullProductClickHandler = () => {
    if (quickViewproductNotAvailableImprovements) {
      return quickViewFullPDPClickEvent();
    }

    return undefined;
  };

  return (
    <>
      <div
        data-testid="quickview-content"
        className={`quickView-content-container ${
          quickViewDrawerEnabled ? 'scroll-quickView-content hmf-pb-s hmf-p-md-m hmf-p-s' : ''
        }`}
        style={
          quickViewDrawerEnabled
            ? {
                height: contentHeight,
              }
            : {}
        }>
        <ProductPromotion
          promotionData={promotionData}
          catentryId={`${quickviewDetails?.style?.parentCatentryId}`}
        />
        <div className="hmf-display-flex hmf-flex-row hmf-flex-grow-none hmf-flex-shrink hmf-justify-content-space-between full-width">
          {storeIdentifierName !== 'g3' && (
            <Badge
              badgeName={style?.badge ?? null}
              storeIdentifierName={storeIdentifierName}
              key={`badge${key}`}
            />
          )}
          {style?.lowStock && template !== 'favorites' && (
            <div
              className="hmf-display-flex hmf-align-items-center hmf-flex-grow-none hmf-flex-shrink hmf-justify-content-center lowStock"
              data-testid="quickViewModallowStock">
              <p className="hmf-subheader-bold-s">Limited Stock</p>
            </div>
          )}
        </div>
        <ImageViewer
          productGroup={quickviewDetails}
          selectedColor={color}
          loading={loading}
          storeIdentifierName={storeIdentifierName}
        />
        {template !== 'favorites' && (
          <div
            className={`hmf-display-flex quickview-price ${redirect && 'center-price'} hmf-my-xs`}>
            <Price
              priceDetails={quickviewDetails.style.prices}
              catentryId={quickviewDetails.style.catentryId}
              storeIdentifierName={storeIdentifierName}
              selectedSKU={sku}
            />
          </div>
        )}
        {showDetailsLink ? (
          <TextLink
            variant="primary"
            href={quickviewDetails.style.pdpSeoUrl}
            onClick={viewFullProductClickHandler}
            size="large"
            className={`hmf-pt-s ${quickViewDrawerEnabled && 'hmf-pl-s'}`}>
            View Full Product Details
          </TextLink>
        ) : (
          <>
            <Attributes
              productGroup={quickviewDetails}
              setColor={setColor}
              sku={sku}
              setSKU={setSKU}
              validate={validate}
              setValidate={setValidate}
              skuDefaultValue={skuDefaultValue}
              setAttributesState={setAttributesState}
            />

            {showAlertBanner && (
              <div className="hmf-mb-m hmf-mt-l">
                <AlertBanner
                  title="Option Not Available"
                  type="warn"
                  customClass="hmf-subheader-bold-s hmf-pt-xxs"
                  message=""
                />
              </div>
            )}

            {showFulfillmentOptions && template === 'cart' && (
              <div className="attribute-groups hmf-display-flex hmf-flex-column">
                <FulfillmentOptions
                  quickviewDetails={quickviewDetails}
                  sku={sku}
                  count={count}
                  selectedMethod={selectedMethod}
                  setSelectedMethod={setSelectedMethod}
                />
                <PreOrderMessaging sku={sku} />
              </div>
            )}
          </>
        )}
      </div>

      <div
        data-testid="quickview-ctas"
        className={`${showDetailsLink && 'hmf-display-none'} ${
          quickViewDrawerEnabled
            ? 'hmf-pt-s hmf-pb-l hmf-px-s quickView-content-footer separator'
            : ''
        } ${redirect && template === 'edit' && 'hmf-pt-s'}`}
        ref={footerRef}>
        {template === 'cart' && (
          <AddToCart
            onClose={onClose}
            count={count}
            selectedSKU={sku}
            validate={validate}
            setValidate={setValidate}
            selectedStoreId={storeDetails?.location ?? storeDetails?.store ?? undefined}
            selectedMethod={selectedMethod}
            quickviewDetails={quickviewDetails}
            storeIdentifierName={storeIdentifierName}
            callback={callback}
            attributesState={attributesState}
            updateSignal={updateSignal}
          />
        )}
        {template === 'favorites' && (
          <AddToFavorites
            template="full"
            onClose={onClose}
            quickviewDetails={quickviewDetails}
            callback={callback}
            attributesState={attributesState}
            selectedSKU={sku}
            updateSignal={updateSignal}
          />
        )}
        {template === 'edit' && (
          <UpdateProduct
            onClose={onClose}
            ecode={key}
            selectedSku={sku}
            skuDefaultValue={skuDefaultValue}
            callback={callback}
            isProductOOS={isProductOOS}
          />
        )}
        {template !== 'edit' && (
          <TextLink
            variant="primary"
            size="large"
            href={quickviewDetails.style.pdpSeoUrl}
            onClick={viewFullProductClickHandler}
            className="hmf-text-align-center hmf-pt-s hmf-display-block">
            View Full Product Details
          </TextLink>
        )}
      </div>
    </>
  );
};

export default QuickViewContent;
